import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import { Toaster } from 'sonner';
import { AnimatePresence } from 'framer-motion';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Components
import { VideoIntro } from './components/VideoIntro';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { DarkModeToggle } from './components/DarkModeToggle';

// Public Pages
import { HomePage } from './pages/HomePage';
import KVICBiogas from './pages/KVICBiogas';
import { AboutPage } from './pages/AboutPage';
import { ServicesPage } from './pages/ServicesPage';
import { CBGgas } from './pages/CBGgas';
import { GalleryPage } from './pages/GalleryPage';
import { ContactPage } from './pages/ContactPage';
import { RBHMPage } from './pages/RBHMPage';
import { RRRProto } from './pages/RRRProto';
import SmartOMBiogas from './pages/SmartOMBiogas';
import { BiogasLabPage } from './pages/BiogasLabPage';
import { WaterSolutionsPage } from './pages/WaterSolutionsPage';
import { Digestate } from './pages/Digestate';
import { Biomass } from './pages/Biomass';
import { CompostingPage } from './pages/CompostingPage';
import { Careers } from './pages/Careers';

// Admin Pages
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminSubscribers } from './pages/admin/AdminSubscribers';
import { AdminContacts } from './pages/admin/AdminContacts';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Layout Component for Public Pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return children;
  }

  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <DarkModeToggle />
    </>
  );
};

// Main App Content
const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  const [showIntro, setShowIntro] = useState(() => {
    const hasSeenIntro = sessionStorage.getItem('renvitek_intro_seen');
    return !hasSeenIntro && !window.location.pathname.startsWith('/admin');
  });

  const [introComplete, setIntroComplete] = useState(() => {
    const hasSeenIntro = sessionStorage.getItem('renvitek_intro_seen');
    return (
      !!hasSeenIntro ||
      window.location.pathname.startsWith('/admin')
    );
  });

  const handleIntroComplete = () => {
    sessionStorage.setItem('renvitek_intro_seen', 'true');
    setShowIntro(false);

    setTimeout(() => {
      setIntroComplete(true);
    }, 100);
  };

  return (
    <>
      <AnimatePresence>
        {showIntro && !isAdminRoute && (
          <VideoIntro onComplete={handleIntroComplete} />
        )}
      </AnimatePresence>

      {(introComplete || isAdminRoute) && (
        <PublicLayout>
          <Routes>

            {/* =====================================================
                PUBLIC ROUTES
            ===================================================== */}

            <Route
              path="/"
              element={<HomePage />}
            />

            <Route
              path="/about"
              element={<AboutPage />}
            />

            <Route
              path="/services"
              element={<ServicesPage />}
            />

            <Route
              path="/services/biogas"
              element={<CBGgas />}
            />

            <Route
              path="/services/rbhm"
              element={<RBHMPage />}
            />

            <Route
              path="/services/RRRProto"
              element={<RRRProto />}
            />

            <Route
              path="/services/kvic-biogas"
              element={<KVICBiogas />}
            />

            {/* =====================================================
                SMART O&M FOR BIOGAS & CBG PLANTS
            ===================================================== */}

            <Route
              path="/services/smart-om-biogas"
              element={<SmartOMBiogas />}
            />

            <Route
              path="/services/lab"
              element={<BiogasLabPage />}
            />

            <Route
              path="/services/water"
              element={<WaterSolutionsPage />}
            />

            <Route
              path="/services/dige"
              element={<Digestate />}
            />

            <Route
              path="/services/biomass"
              element={<Biomass />}
            />

            <Route
              path="/services/composting"
              element={<CompostingPage />}
            />

            <Route
              path="/careers"
              element={<Careers />}
            />

            <Route
              path="/gallery"
              element={<GalleryPage />}
            />

            <Route
              path="/contact"
              element={<ContactPage />}
            />

            {/* =====================================================
                ADMIN ROUTES
            ===================================================== */}

            <Route
              path="/admin"
              element={<AdminLoginPage />}
            />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/subscribers"
              element={
                <ProtectedRoute>
                  <AdminSubscribers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/contacts"
              element={
                <ProtectedRoute>
                  <AdminContacts />
                </ProtectedRoute>
              }
            />

            {/* =====================================================
                CATCH-ALL REDIRECT
            ===================================================== */}

            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />

          </Routes>
        </PublicLayout>
      )}
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>

          <div className="App">

            <AppContent />

            <Toaster
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                style: {
                  background: '#064e3b',
                  color: 'white',
                  border: 'none'
                }
              }}
            />

          </div>

        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
